import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 px-2 sm:px-6 md:px-8 w-full overflow-x-hidden">
      <div className="max-w-3xl sm:max-w-4xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-emerald-600 hover:text-emerald-800 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Home
          </Link>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">Terms of Service</h1>
          <p className="text-gray-600 mb-4 sm:mb-8 text-sm sm:text-base">Last updated: July 2, 2025</p>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-3 py-4 sm:p-6">
            <div className="prose max-w-none text-xs sm:text-sm md:text-base">
              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  Welcome to BioHaz Waste Solutions. These Terms of Service ("Terms") govern your access to and use of our website and services. By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">2. Services</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  BioHaz Waste Solutions provides professional biohazardous and hazardous waste management services, including but not limited to waste collection, transportation, treatment, and disposal. Our services are subject to availability and may be modified or discontinued at our discretion.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">3. User Responsibilities</h2>
                <p className="text-gray-700 mb-2">
                  When using our services, you agree to:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 text-gray-700 space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                  <li>Provide accurate and complete information when requesting services</li>
                  <li>Comply with all applicable laws and regulations regarding waste management</li>
                  <li>Properly segregate and package waste according to our guidelines</li>
                  <li>Not use our services for any illegal or unauthorized purpose</li>
                  <li>Not interfere with or disrupt our services or servers</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">4. Intellectual Property</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  All content included on our website, such as text, graphics, logos, images, and software, is the property of BioHaz Waste Solutions or its content suppliers and is protected by copyright and other intellectual property laws. You may not use, reproduce, modify, or distribute any content from our website without our prior written permission.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">5. Limitation of Liability</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  To the maximum extent permitted by law, BioHaz Waste Solutions shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
                </p>
                <ul className="list-disc pl-5 sm:pl-6 text-gray-700 space-y-1 sm:space-y-2 mb-3 sm:mb-4">
                  <li>Your access to or use of or inability to access or use our services</li>
                  <li>Any conduct or content of any third party on our services</li>
                  <li>Any unauthorized access, use, or alteration of your transmissions or content</li>
                  <li>Any other matter relating to our services</li>
                </ul>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">6. Indemnification</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  You agree to defend, indemnify, and hold harmless BioHaz Waste Solutions and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of our services or your violation of these Terms.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">7. Governing Law</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of Uganda, without regard to its conflict of law provisions. Any dispute arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Kampala, Uganda.
                </p>
              </section>

              <section className="mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">8. Changes to Terms</h2>
                <p className="text-gray-700 mb-2 sm:mb-4">
                  We reserve the right to modify or replace these Terms at any time at our sole discretion. We will provide notice of any changes by posting the updated Terms on our website and updating the "Last updated" date at the top of these Terms. Your continued use of our services after any such changes constitutes your acceptance of the new Terms.
                </p>
              </section>

              <section>
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-2 sm:mb-4">9. Contact Us</h2>
                <p className="text-gray-700">
                  If you have any questions about these Terms, please contact us at:
                </p>
                <p className="text-gray-700 mt-2">
                  Email: <a href="mailto:info@biohazwastesolutions.com" className="text-emerald-600 hover:underline">info@biohazwastesolutions.com</a><br />
                  Phone: +256 393 254458 / +256 782 833711
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
